import { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import type { LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import { LieferantSearchField } from "@/components/LieferantSearchField";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  getArtikelDetail,
  getArtikelStammdatenVorschlaege,
  updateArtikel,
  type ArtikelUpdatePayload,
} from "@/lib/artikel";
import { saveArtikelRabatt, type ArtikelRabattState } from "@/lib/artikelRabatt";
import { saveArtikelZuschlag, type ArtikelZuschlagState } from "@/lib/artikelZuschlag";
import {
  ArtikelRabattSection,
  previewEk as previewRabattEk,
  previewRabattPct,
  rabattSelectionToSaveBody,
  type RabattSelection,
} from "@/pages/artikel/ArtikelRabattSection";
import {
  ArtikelZuschlagSection,
  selectionToSaveBody,
  type ZuschlagSelection,
} from "@/pages/artikel/ArtikelZuschlagSection";
import {
  INACTIVE_RABATT,
  INACTIVE_ZUSCHLAG,
  activateRabatt,
  activateZuschlag,
  exclusivePriceSelectionsFromState,
  isRabattActive,
  isZuschlagActive,
} from "@/pages/artikel/artikelPriceSelection";
import { ArtikelSuggestField } from "@/pages/artikel/ArtikelSuggestField";
import { UnitAutocompleteField } from "@/pages/dokumente/UnitAutocompleteField";

function parseNum(raw: string): number | undefined {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

function pendingOrValue(handle: LockedAutocompleteHandle | null, committed: string): string {
  return (handle?.getPendingQuery() || committed).trim();
}

export function ArtikelEditDialog({
  open,
  artikelId,
  onClose,
  onSaved,
}: {
  open: boolean;
  artikelId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [heroNr, setHeroNr] = useState("");
  const [name, setName] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [einheit, setEinheit] = useState("");
  const [ean, setEan] = useState("");
  const [matchcode, setMatchcode] = useState("");
  const [hersteller, setHersteller] = useState("");
  const [herstellerNr, setHerstellerNr] = useState("");
  const [lieferantenNr, setLieferantenNr] = useState("");
  const [warengruppe, setWarengruppe] = useState("");
  const [lieferantName, setLieferantName] = useState("");
  const [lieferantGpId, setLieferantGpId] = useState<number | null>(null);
  const [preisBrutto, setPreisBrutto] = useState("");
  const [rabatt, setRabatt] = useState("");
  const [preisNetto, setPreisNetto] = useState("");
  const [preismenge, setPreismenge] = useState("");
  const [preisEinheit, setPreisEinheit] = useState("");
  const [preisGueltigBis, setPreisGueltigBis] = useState("");
  const [listenpreis, setListenpreis] = useState<number | null>(null);
  const [zuschlagState, setZuschlagState] = useState<ArtikelZuschlagState | null>(null);
  const [zuschlagSelection, setZuschlagSelection] = useState<ZuschlagSelection>(INACTIVE_ZUSCHLAG);
  const [rabattState, setRabattState] = useState<ArtikelRabattState | null>(null);
  const [rabattSelection, setRabattSelection] = useState<RabattSelection>(INACTIVE_RABATT);
  const [matchcodes, setMatchcodes] = useState<string[]>([]);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const matchcodeRef = useRef<LockedAutocompleteHandle>(null);
  const kategorieRef = useRef<LockedAutocompleteHandle>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const detail = await getArtikelDetail(id);
      const a = detail.artikel;
      const p = detail.preise?.[0];
      setHeroNr(str(a.hero_nr));
      setName(str(a.hero_base_name));
      setBeschreibung(str(a.hero_base_description));
      setEinheit(str(a.hero_base_unit_type));
      setEan(str(a.hero_base_ean));
      setMatchcode(str(a.hero_base_matchcode));
      setHersteller(str(a.hero_base_manufacturer));
      setHerstellerNr(str(a.hero_base_manufacturer_nr));
      setLieferantenNr(str(a.hero_base_supplier_nr));
      setWarengruppe(str(a.hero_base_category));
      setLieferantName(str(detail.lieferant?.firmenname ?? a.hero_supply_operator_name));
      setLieferantGpId(detail.lieferant?.id ?? null);
      setPreisBrutto(p?.bruttopreis != null ? String(p.bruttopreis) : "");
      setRabatt(p?.rabatt != null ? String(p.rabatt) : "");
      setPreisNetto(p?.nettopreis != null ? String(p.nettopreis) : "");
      setPreismenge(p?.preismenge != null ? String(p.preismenge) : "");
      setPreisEinheit(str(p?.einheit));
      setPreisGueltigBis(
        p?.gueltig_bis ? String(p.gueltig_bis).slice(0, 10) : "",
      );
      setListenpreis(
        a.hero_list_price != null && Number(a.hero_list_price) > 0
          ? Number(a.hero_list_price)
          : null,
      );
      const zs = detail.zuschlag ?? null;
      const rs = detail.rabatt ?? null;
      const exclusive = exclusivePriceSelectionsFromState(rs, zs);
      setZuschlagState(zs);
      setZuschlagSelection(exclusive.zuschlag);
      setRabattState(rs);
      setRabattSelection(exclusive.rabatt);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Artikel konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && artikelId != null) void load(artikelId);
  }, [open, artikelId, load]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getArtikelStammdatenVorschlaege()
      .then((data) => {
        if (cancelled) return;
        setMatchcodes(data.matchcodes);
        setKategorien(data.kategorien);
      })
      .catch(() => {
        if (!cancelled) {
          setMatchcodes([]);
          setKategorien([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleSave() {
    if (artikelId == null) return;
    const rabattOn = isRabattActive(rabattSelection);
    const zuschlagOn = isZuschlagActive(zuschlagSelection);
    if (rabattOn && zuschlagOn) {
      setError("Bitte entweder einen Rabatt oder einen Zuschlag auswählen.");
      return;
    }
    const rabattBody = rabattOn ? rabattSelectionToSaveBody(rabattSelection) : null;
    if (rabattBody && "error" in rabattBody) {
      setError(rabattBody.error);
      return;
    }
    const bodyOrError = zuschlagOn ? selectionToSaveBody(zuschlagSelection) : null;
    if (bodyOrError && "error" in bodyOrError) {
      setError(bodyOrError.error);
      return;
    }

    setSaving(true);
    setError(null);
    const payload: ArtikelUpdatePayload = {
      hero_nr: heroNr.trim() || undefined,
      name: name.trim() || undefined,
      beschreibung: beschreibung.trim() || undefined,
      einheit: einheit.trim() || undefined,
      ean: ean.trim() || undefined,
      matchcode: pendingOrValue(matchcodeRef.current, matchcode) || undefined,
      hersteller: hersteller.trim() || undefined,
      hersteller_nr: herstellerNr.trim() || undefined,
      lieferanten_nr: lieferantenNr.trim() || undefined,
      warengruppe: pendingOrValue(kategorieRef.current, warengruppe) || undefined,
      lieferant_name: lieferantName.trim() || undefined,
      lieferant_geschaeftspartner_id: lieferantGpId,
      preis_brutto: parseNum(preisBrutto) ?? null,
      rabatt: parseNum(rabatt),
      preis_netto: parseNum(preisNetto),
      preismenge: parseNum(preismenge) != null ? Math.round(parseNum(preismenge)!) : undefined,
      preis_einheit: preisEinheit.trim() || undefined,
      preis_gueltig_bis: preisGueltigBis.trim() || null,
    };
    try {
      await updateArtikel(artikelId, payload);
      if (rabattBody && !("error" in rabattBody)) {
        await saveArtikelRabatt(artikelId, rabattBody);
      }
      if (bodyOrError && !("error" in bodyOrError)) {
        await saveArtikelZuschlag(artikelId, bodyOrError);
      }
      toast.success("Artikel gespeichert.");
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const listenpreisOverride =
    listenpreis != null && listenpreis > 0
      ? listenpreis
      : (parseNum(preisBrutto) ?? null);
  const rabattPreviewEk = previewRabattEk(
    rabattSelection,
    listenpreisOverride,
    rabattState?.staffeln ?? [],
  );
  const rabattPreviewPct = previewRabattPct(rabattSelection, rabattState?.staffeln ?? []);
  const ekOverride = rabattPreviewEk ?? parseNum(preisNetto) ?? null;

  function handleRabattChange(next: RabattSelection) {
    const exclusive = activateRabatt(next);
    setRabattSelection(exclusive.rabatt);
    setZuschlagSelection(exclusive.zuschlag);
    const pct = previewRabattPct(next, rabattState?.staffeln ?? []);
    const ek = previewRabattEk(next, listenpreisOverride, rabattState?.staffeln ?? []);
    if (pct != null) setRabatt(String(pct));
    if (ek != null) setPreisNetto(String(ek));
  }

  function handleZuschlagChange(next: ZuschlagSelection) {
    const exclusive = activateZuschlag(next);
    setRabattSelection(exclusive.rabatt);
    setZuschlagSelection(exclusive.zuschlag);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Artikel bearbeiten"
      size="2xl"
      bodyClassName="overflow-y-auto max-h-[75vh]"
    >
      <div className="space-y-4">
        {error ? <Alert variant="error">{error}</Alert> : null}
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <>
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-label">Stammdaten</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput label="Artikelnummer" value={heroNr} onChange={(e) => setHeroNr(e.target.value)} />
                <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                <UnitAutocompleteField
                  label="Einheit"
                  value={einheit}
                  onChange={setEinheit}
                />
                <TextInput label="EAN" value={ean} onChange={(e) => setEan(e.target.value)} />
                <ArtikelSuggestField
                  label="Matchcode"
                  value={matchcode}
                  onChange={setMatchcode}
                  suggestions={matchcodes}
                  fieldRef={matchcodeRef}
                />
                <ArtikelSuggestField
                  label="Kategorie"
                  value={warengruppe}
                  onChange={setWarengruppe}
                  suggestions={kategorien}
                  fieldRef={kategorieRef}
                />
                <TextInput label="Hersteller" value={hersteller} onChange={(e) => setHersteller(e.target.value)} />
                <TextInput label="Hersteller-Nr." value={herstellerNr} onChange={(e) => setHerstellerNr(e.target.value)} />
              </div>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-label">Beschreibung</span>
                <textarea
                  className="min-h-[80px] w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm"
                  value={beschreibung}
                  onChange={(e) => setBeschreibung(e.target.value)}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <LieferantSearchField
                  label="Lieferant"
                  value={lieferantName}
                  selectedGpId={lieferantGpId}
                  onChange={({ name, gpId }) => {
                    setLieferantName(name);
                    setLieferantGpId(gpId);
                  }}
                />
                <TextInput
                  label="Lieferanten-Nr."
                  value={lieferantenNr}
                  onChange={(e) => setLieferantenNr(e.target.value)}
                  hint="Artikelnummer beim Lieferanten. Unabhängig von der Hersteller-Nr."
                />
              </div>
            </section>

            <section className="space-y-3 border-t border-separator pt-4">
              <h3 className="text-sm font-semibold text-label">Einkaufspreis (neuer Preissatz)</h3>
              <p className="text-xs text-label-secondary">
                Geänderte Preise werden als neuer Preissatz gespeichert. Bestehende Preishistorie bleibt erhalten.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <TextInput label="Bruttopreis (EK)" value={preisBrutto} onChange={(e) => setPreisBrutto(e.target.value)} />
                <TextInput label="Rabatt %" value={rabatt} onChange={(e) => setRabatt(e.target.value)} />
                <TextInput label="Nettopreis (EK)" value={preisNetto} onChange={(e) => setPreisNetto(e.target.value)} />
                <TextInput label="Preismenge" value={preismenge} onChange={(e) => setPreismenge(e.target.value)} />
                <UnitAutocompleteField
                  label="Preiseinheit"
                  value={preisEinheit}
                  onChange={setPreisEinheit}
                />
                <TextInput
                  label="Gültig bis"
                  type="date"
                  value={preisGueltigBis}
                  onChange={(e) => setPreisGueltigBis(e.target.value)}
                  hint="Leer = unbegrenzt. Nach Ablauf: 0 € in Dokumenten, alter Preis nur als Orientierung."
                />
              </div>
            </section>

            <div className="space-y-6 border-t border-separator pt-4">
              <ArtikelRabattSection
                state={rabattState}
                selection={rabattSelection}
                onChange={handleRabattChange}
                disabled={saving}
                listenpreisOverride={listenpreisOverride}
              />
              <ArtikelZuschlagSection
                state={zuschlagState}
                selection={zuschlagSelection}
                onChange={handleZuschlagChange}
                disabled={saving}
                ekOverride={ekOverride}
                listenpreis={listenpreisOverride}
                rabatt={rabattPreviewPct ?? parseNum(rabatt) ?? null}
              />
            </div>

            <div className="flex justify-end gap-2 border-t border-separator pt-4">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Abbrechen
              </Button>
              <Button onClick={() => void handleSave()} disabled={saving}>
                {saving ? <Spinner className="h-4 w-4" /> : "Speichern"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
